import { formatDate, formatRelative, formatTime, type StatusLevel } from '@westhetics/ui'
import type { Session } from '@/lib/session'
import type {
  Anamnesis,
  AnamnesisChannel,
  AnamnesisDelivery,
  AnamnesisDraft,
  AnamnesisStatus,
  AnamnesisSummary,
  CopyEvent,
  DeliveryEvent,
  RecordIssue,
} from './api'

/**
 * Como a anamnese é escrita na tela (docs/areas/anamnese.md, "Estados e transições" e RN-ANA-03;
 * docs/styleguide/04-escrita.md). A etiqueta por extenso vem pronta da API em `label`; aqui ficam o
 * nível da etiqueta, o canal, a entrega e as frases derivadas.
 */

/** "Link vencido" é a única preenchida em Plum; "Não preenchida" depende de uma ação da clínica. */
export const STATUS_LEVELS: Record<AnamnesisStatus, StatusLevel> = {
  issued: 'attention',
  in_progress: 'attention',
  completed: 'done',
  expired: 'critical',
  canceled: 'waiting',
}

/** `not_completed` é filtro, nunca status: emitida, em preenchimento ou vencida (P-ANA-15). */
export function isNotCompleted(status: AnamnesisStatus) {
  return status === 'issued' || status === 'in_progress' || status === 'expired'
}

export const CHANNELS: readonly AnamnesisChannel[] = ['whatsapp', 'email', 'both', 'link']

export const CHANNEL_LABELS: Record<AnamnesisChannel, string> = {
  whatsapp: 'WhatsApp',
  email: 'E-mail',
  both: 'Ambos',
  link: 'Só o link',
}

/** O canal dentro de uma frase: "Enviando por WhatsApp e e-mail". */
export const CHANNEL_IN_SENTENCE: Record<AnamnesisChannel, string> = {
  whatsapp: 'WhatsApp',
  email: 'e-mail',
  both: 'WhatsApp e e-mail',
  link: 'link',
}

const DELIVERY_LABELS: Record<AnamnesisDelivery['channel'], Record<DeliveryEvent, string>> = {
  whatsapp: {
    sent: 'WhatsApp · enviado',
    delivered: 'WhatsApp · entregue',
    read: 'WhatsApp · lido',
    failed: 'WhatsApp · não entregue',
  },
  email: {
    sent: 'E-mail · enviado',
    delivered: 'E-mail · entregue',
    read: 'E-mail · aberto',
    failed: 'e-mail devolvido',
  },
}

/** O último evento de cada canal, do mais antigo canal para o mais novo: "WhatsApp · lido". */
export function latestDeliveries(delivery: readonly AnamnesisDelivery[]) {
  const latest = new Map<AnamnesisDelivery['channel'], AnamnesisDelivery>()
  for (const event of delivery) {
    const current = latest.get(event.channel)
    if (!current || new Date(event.at).getTime() >= new Date(current.at).getTime()) {
      latest.set(event.channel, event)
    }
  }
  return [...latest.values()].map((event) => ({
    ...event,
    label: DELIVERY_LABELS[event.channel][event.event],
  }))
}

/** A linha de entrega da lista: "WhatsApp · lido"; no canal `link`, nada foi enviado. */
export function deliveryLine(anamnesis: Pick<AnamnesisSummary, 'channel' | 'delivery'>) {
  const events = latestDeliveries(anamnesis.delivery)
  if (events.length > 0) return events.map((event) => event.label).join(' · ')
  if (anamnesis.channel === 'link') return CHANNEL_LABELS.link
  return null
}

/** "28/04/2026 09:51", como a coluna ENVIO do protótipo. */
export function formatDateTime(iso: string) {
  const date = new Date(iso)
  return `${formatDate(date)} ${formatTime(date)}`
}

/** A data da migalha da leitura: quando a paciente enviou. */
export function readingDate(anamnesis: Anamnesis) {
  const iso = anamnesis.evidence?.submittedAt ?? anamnesis.completedAt ?? anamnesis.createdAt
  return formatDate(new Date(iso))
}

const SECTION_POSITIONS: Record<string, number> = {
  aboutYou: 1,
  yourHealth: 2,
  yourExpectations: 3,
  selfImage: 4,
  confirmation: 5,
}

/** O bloco em que a paciente parou, de 1 a 5; `null` em chave que a tela não conhece. */
export function draftSectionPosition(draft: AnamnesisDraft | null | undefined) {
  return draft ? (SECTION_POSITIONS[draft.cursor.sectionKey] ?? null) : null
}

/** "Bloco 2 de 5 · última resposta há 3 horas" (o rascunho como a clínica o vê, RN-ANA-08). */
export function draftLine(draft: AnamnesisDraft, now = new Date()) {
  const position = draftSectionPosition(draft)
  const saved = formatRelative(new Date(draft.savedAt), now)
  const savedLine = /^\d/.test(saved) ? `última resposta em ${saved}` : `última resposta ${saved}`
  return position ? `Bloco ${position} de 5 · ${savedLine}` : savedLine
}

/** "em preenchimento desde 20/09", quando a API diz desde quando. */
export function draftSince(draft: AnamnesisDraft | null | undefined) {
  if (!draft?.startedAt) return null
  return `em preenchimento desde ${formatDate(new Date(draft.startedAt), { year: false })}`
}

/** O link ainda vale? A etiqueta "Link vencido" já vem da API; isto decide o que a tela oferece. */
export function isLinkExpired(
  anamnesis: Pick<AnamnesisSummary, 'status' | 'link'>,
  now = new Date(),
) {
  return (
    anamnesis.status === 'expired' || new Date(anamnesis.link.expiresAt).getTime() < now.getTime()
  )
}

/** "3 respostas pedem atenção antes do procedimento" · "1 resposta pede atenção…" */
export function attentionTitle(count: number) {
  return count === 1
    ? '1 resposta pede atenção antes do procedimento'
    : `${count} respostas pedem atenção antes do procedimento`
}

const RECORD_FIELD_LABELS: Record<string, string> = {
  name: 'nome',
  occupation: 'profissão',
  maritalStatus: 'estado civil',
  email: 'e-mail',
  whatsapp: 'WhatsApp',
  address: 'endereço',
  guardian: 'responsável',
  docNumber: 'CPF',
  foreignDocument: 'documento',
  birthDate: 'data de nascimento',
}

function joinList(items: string[]) {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]}`
}

/** "A paciente atualizou e-mail e endereço nesta anamnese" (RN-ANA-07); `null` sem mudança. */
export function recordChangesSentence(changes: readonly { field: string }[] | undefined) {
  const labels: string[] = []
  for (const change of changes ?? []) {
    const root = change.field.split('.')[0] ?? change.field
    const label = RECORD_FIELD_LABELS[root] ?? root
    if (!labels.includes(label)) labels.push(label)
  }
  if (labels.length === 0) return null
  return `A paciente atualizou ${joinList(labels)} nesta anamnese`
}

const RECORD_ISSUE_SENTENCES: Record<RecordIssue, string> = {
  doc_number_in_other_record: 'O CPF informado nesta anamnese já está em outro cadastro.',
  email_in_other_record: 'O e-mail informado nesta anamnese já está em outro cadastro.',
}

export function recordIssueSentence(issue: RecordIssue) {
  return (
    RECORD_ISSUE_SENTENCES[issue] ?? 'Um dado informado nesta anamnese já está em outro cadastro.'
  )
}

/** Para onde a cópia foi e o que o provedor devolveu (RN-ANA-14). */
export const COPY_EVENT_LABELS: Record<CopyEvent, string> = {
  pending: 'na fila de envio',
  sent: 'enviada',
  delivered: 'entregue',
  bounced: 'e-mail devolvido',
}

/** A prestadora que a pessoa é na clínica ativa, quando o perfil é prestador (RT-02, "seus"). */
export function sessionPractitionerId(session: Session) {
  const profile = session.profile
  if (!profile) return null
  return profile.clinics.find((clinic) => clinic.id === profile.clinicId)?.practitionerId ?? null
}

/** Item de clínica irmã (RT-06): leitura, sem escrita. */
export function isFromSisterClinic(anamnesis: Pick<AnamnesisSummary, 'clinic'>) {
  return Boolean(anamnesis.clinic)
}

/** A leitura é de quem pode (RT-02); o registro da não preenchida, de todos. */
export function canOpen(anamnesis: Pick<AnamnesisSummary, 'status' | 'canRead'>) {
  return anamnesis.status !== 'completed' || anamnesis.canRead
}

/** Reenviar é de todos os perfis, só em não preenchida e só na própria clínica (RN-ANA-04). */
export function canResend(anamnesis: Pick<AnamnesisSummary, 'status' | 'clinic'>) {
  return isNotCompleted(anamnesis.status) && !isFromSisterClinic(anamnesis)
}

/**
 * Cancelar (P-ANA-16): recepção, gestora e root; a prestadora, só as suas. O que a tela não sabe
 * ("usada em atendimento em que ela é a responsável") a API decide, e responde 403.
 */
export function canCancel(
  session: Session,
  anamnesis: Pick<AnamnesisSummary, 'status' | 'clinic' | 'practitioner'>,
) {
  if (!canResend(anamnesis)) return false
  if (session.profile?.role !== 'practitioner') return true
  const own = sessionPractitionerId(session)
  return own !== null && anamnesis.practitioner?.practitionerId === own
}

/** Copia para a área de transferência; `false` quando o navegador não deixa. */
export async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}
